import { Component, inject, Input } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { NgForOf, NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import {
  FormArray,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { ActivatedRoute, Router } from '@angular/router';
import { AppUtilityService } from '../../../utility/app.utility';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { yearValidator } from '../../../validators/year.validator';
import { patchState } from '@ngrx/signals';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { DataLayer } from '../../../models/data-layer';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { DataLayerType } from '../../../models/data-layer-type';
import { WorkspaceDropdownComponent } from '../../atoms/workspace-dropdown/workspace-dropdown.component';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { GlobalsStore } from '../../../stores/globals.state';
import { TooltipModule } from 'primeng/tooltip';

import { IconDropdownComponent } from '../../atoms/icon-dropdown/icon-dropdown.component';
import { TagDropdownComponent } from '../../atoms/tag-dropdown/tag-dropdown.component';

@Component({
  selector: 'app-data-layer-type-form',
  standalone: true,
  imports: [
    ButtonModule,
    ColorPickerModule,
    DividerModule,
    DropdownModule,
    FormFieldWrapperComponent,
    InputTextModule,
    InputNumberModule,
    NgForOf,
    PaginatorModule,
    ReactiveFormsModule,
    InputTextareaModule,
    NgIf,
    WorkspaceDropdownComponent,
    FormFooterComponent,
    TooltipModule,
    IconDropdownComponent,
    TagDropdownComponent,
  ],
  templateUrl: './data-layer-type-form.component.html',
  styleUrl: './data-layer-type-form.component.scss',
})
export class DataLayerTypeFormComponent extends BaseFormComponent<DataLayerType> {
  readonly store = inject(DataLayerTypeStore);
  readonly globalsStore = inject(GlobalsStore);
  readonly dataLayerStore = inject(DataLayerStore);
  @Input() allowInitialYears = true;

  typeOptions = [
    { label: 'Raum (Polygon)', value: 'Polygon' },
    { label: 'Standort (Point)', value: 'Point' },
    { label: 'POI', value: 'POI' },
  ];

  initialYears: DataLayer[] = [];

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
  }

  /**
   * Initializes the form for editing or creating a new data layer type.
   * Sets up the form's structure and pre-fills it with data when editing an existing item.
   * Adds additional controls if creating a new item.
   *
   * @return*/
  initializeForm() {
    this.itemToEdit = this.store.selectedItem();

    this.form = this.fb.group({
      name: [this.itemToEdit?.name || '', Validators.required],
      geoSource: [this.itemToEdit?.geoSource || ''],
      workspaces: [this.itemToEdit?.workspaces || []],
      defaultLayer: [this.itemToEdit?.defaultLayer ?? null],
      type: [this.itemToEdit?.type || 'Polygon', Validators.required],
      color: [this.itemToEdit?.color || '#000000'],
      icon: [this.itemToEdit?.icon || ''],
      tags: [this.itemToEdit?.tags || []],
      initialYears: this.fb.array([]), // Always add the initialYears control
    });

    this.formInitialized = true;
    if (!this.itemToEdit) {
      this.addYear();
    }
  }

  /**
   * Retrieves the 'initialYears' FormArray from the dataLayerTypeForm.
   *
   * @return {FormArray} The FormArray representing the 'initialYears' form control.
   */
  public get initialYearsFormArray(): FormArray {
    return this.form.get('initialYears') as FormArray;
  }

  /**
   * Retrieves an array of plain years extracted from the form control values.
   */
  public get plainYears(): number[] {
    return this.initialYearsFormArray.value.map(
      (yearControl: { year: number }) => {
        return +yearControl.year;
      }
    );
  }

  /**
   * Adds a new year entry to the form array with a predefined validation.
   * It creates a form group containing a year field with a custom validator
   * and pushes it to the initialYearsFormArray.
   *
   * @return {void} This method does not return anything.
   */
  addYear(): void {
    const group = this.fb.group({
      year: ['', yearValidator(this.plainYears)],
    });

    this.initialYearsFormArray.push(group);
  }

  /**
   * Removes a year entry at the specified index from the form array.
   *
   * @param {number} index - The index of the year entry to remove.
   * @return {void} This method does not return anything.
   */
  removeYear(index: number): void {
    this.initialYearsFormArray.removeAt(index);
  }

  /**
   * remove data-layer-type confirmation
   * @param item
   */
  removeDataLayerType(item: DataLayerType) {
    this.confirmationDialogService.confirmDataLayerParentDeletion(
      undefined,
      item,
      () => {
        item.message = {
          severity: 'success',
          summary: 'Löschen erfolgreich!',
          detail: `"${item.name}" und die zugehörigen Jahre, sowie Daten wurden gelöscht.`,
          life: 3000,
        };
        this.remove(item);
        this.routingService.navigateBack(this.cancelLevel);
      }
    );
  }

  onSubmit() {
    if (this.form.valid) {
      if (this.initialYearsFormArray) {
        this.initialYears = this.initialYearsFormArray.value;
        patchState(this.store, {
          triggerOptions: {
            initialYears: this.initialYears,
            type: this.form.value.type,
          },
        });
      }
    }
    this.submit();
  }
}
